import React, { useEffect, useState } from 'react';
import Styles from '../../assets/css/components/warehouse/warehouseEdit.module.scss';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setShouldRefresh } from '../../features/warehouse/warehouseSlice';
import { popUpBoxTrigger } from '../../features/message/popUpSlice';
import ValidationHandler from '../../utils/validationHandler';
import PopUpMessage from '../../utils/errors/PopUpMessage';
import Loader from '../../utils/Loader';
import { getWarehouseById, updateWarehouse } from '../../services/Api';
import { AiOutlineLoading } from 'react-icons/ai';

const WarehouseEdit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [originalData, setOriginalData] = useState(null);

    // Default values for dropdowns
    const defaultCountries = [
        { id: '1', name: 'India' },
        { id: '2', name: 'USA' }
    ];

    const defaultStates = {
        '1': [
            { id: '1', name: 'Gujarat' },
            { id: '2', name: 'Maharashtra' },
            { id: '3', name: 'Rajasthan' }
        ],
        '2': [
            { id: '4', name: 'California' },
            { id: '5', name: 'Texas' },
            { id: '6', name: 'Florida' }
        ]
    };

    const defaultCities = {
        '1': [
            { id: '1', name: 'Ahmedabad' },
            { id: '2', name: 'Surat' },
            { id: '3', name: 'Vadodara' }
        ],
        '2': [
            { id: '4', name: 'Mumbai' },
            { id: '5', name: 'Pune' },
            { id: '6', name: 'Nagpur' }
        ]
    };

    const [formData, setFormData] = useState({
        name: '',
        address: '',
        country_id: '',
        state_id: '',
        city_id: '',
        status: ''
    });

    const [errors, setErrors] = useState({});

    // Fetch warehouse data on component mount
    useEffect(() => {
        const fetchWarehouseData = async () => {
            setIsLoading(true);
            try {
                const response = await getWarehouseById(id);
                if (response.status === 200) {
                    const data = response.data;
                    setOriginalData(data);
                    setFormData({
                        name: data.name,
                        address: data.address,
                        country_id: data.country_id,
                        state_id: data.state_id,
                        city_id: data.city_id,
                        status: data.status
                    });
                }
            } catch (error) {
                showPopup('error', 'Error', 'Failed to fetch warehouse details');
            } finally {
                setIsLoading(false);
            }
        };
        fetchWarehouseData();
    }, [id]);

    const validationRules = {
        name: {
            required: true,
            message: 'Name is required'
        },
        address: {
            required: true,
            message: 'Address is required'
        },
        country_id: {
            required: true,
            message: 'Country is required'
        },
        state_id: {
            required: true,
            message: 'State is required'
        },
        city_id: {
            required: true,
            message: 'City is required'
        },
        status: {
            required: true,
            message: 'Please select a status'
        }
    };

    const showPopup = (type, heading, message) => {
        dispatch(popUpBoxTrigger({
            type,
            heading,
            message,
            show: true
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        const selectElement = e.target;
        if (value) {
            selectElement.style.color = '#000000';
        } else {
            selectElement.style.color = '#797775';
        }

        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const response = await updateWarehouse({
                id: id,
                ...formData
            });

            if (response.status === 200) {
                showPopup('success', 'Success', 'Warehouse updated successfully');
                dispatch(setShouldRefresh(true));
                await new Promise(resolve => setTimeout(resolve, 1000));
                navigate("/warehouse");
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to update warehouse';
            showPopup('error', 'Error', errorMessage);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={`${Styles.warehouse_edit}`}>
            <PopUpMessage />
            <div className={`${Styles.heading}`}>
                <h2>Warehouse Information</h2>
            </div>

            <div className={`${Styles.warehouse_form}`}>
                {isLoading ? (
                    <div className={`${Styles.form_loader_container}`}>
                        <div>
                            <Loader size="large" color="primary" />
                        </div>
                    </div>
                ) : (
                    <div className={`${Styles.warehouse_details}`}>
                        <div className={`${Styles.title}`}>
                            <h4>Warehouse details</h4>
                            <hr />
                        </div>
                        <div className={`${Styles.form}`}>
                            <div className={`${Styles.label_input} ${errors.name ? Styles.error : ''}`}>
                                <label>Warehouse Name</label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        placeholder="Enter Warehouse Name"
                                        onChange={handleInputChange}
                                    />
                                    {errors.name && <p className={`${Styles.errorMessage}`}>{errors.name}</p>}
                                </div>
                            </div>

                            <div className={`${Styles.label_input} ${errors.address ? Styles.error : ''}`}>
                                <label>Warehouse Address</label>
                                <div className={`${Styles.select_field}`}>
                                    <input
                                        type="text"
                                        name="address"
                                        value={formData.address}
                                        placeholder="Enter Address"
                                        onChange={handleInputChange}
                                    />
                                    {errors.address && <p className={`${Styles.errorMessage}`}>{errors.address}</p>}
                                </div>
                            </div>

                            <div className={`${Styles.label_input} ${errors.country_id ? Styles.error : ''}`}>
                                <label>Country /State /City</label>
                                <div className={`${Styles.select_container}`}>
                                    <div className={`${Styles.country} ${errors.country_id ? Styles.error : ''}`}>
                                        <select
                                            className={`${Styles.country} ${errors.country_id ? Styles.error : ''}`}
                                            name="country_id"
                                            value={formData.country_id}
                                            onChange={handleInputChange}
                                        >
                                            <option value="" hidden>Select Country</option>
                                            {defaultCountries.map((country) => (
                                                <option key={country.id} value={country.id}>
                                                    {country.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.country_id && <p className={`${Styles.errorMessage}`}>{errors.country_id}</p>}
                                    </div>
                                    <div className={`${Styles.state} ${errors.state_id ? Styles.error : ''}`}>
                                        <select
                                            className={`${Styles.state}`}
                                            name="state_id"
                                            value={formData.state_id}
                                            onChange={handleInputChange}
                                        >
                                            <option value="" hidden>Select State</option>
                                            {defaultStates[formData.country_id]?.map((state) => (
                                                <option key={state.id} value={state.id}>
                                                    {state.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.state_id && <p className={`${Styles.errorMessage}`}>{errors.state_id}</p>}
                                    </div>
                                    <div className={`${Styles.city} ${errors.city_id ? Styles.error : ''}`}>
                                        <select
                                            className={`${Styles.city}`}
                                            name="city_id"
                                            value={formData.city_id}
                                            onChange={handleInputChange}
                                        >
                                            <option value="" hidden>Select City</option>
                                            {defaultCities[formData.state_id]?.map((city) => (
                                                <option key={city.id} value={city.id}>
                                                    {city.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.city_id && <p className={`${Styles.errorMessage}`}>{errors.city_id}</p>}
                                    </div>
                                </div>
                            </div>

                            <div className={`${Styles.label_input} ${errors.status ? Styles.error : ''}`}>
                                <label>Status</label>
                                <div className={`${Styles.select_field}`}>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        className={`${errors.status ? Styles.error : ''}`}
                                    >
                                        <option value="" disabled hidden>Select Status</option>
                                        <option value="1">Active</option>
                                        <option value="0">Inactive</option>
                                    </select>
                                    {errors.status && <p className={`${Styles.errorMessage}`}>{errors.status}</p>}
                                </div>
                            </div>
                        </div>

                        <div className={`${Styles.buttons}`}>
                            <button onClick={handleSubmit} disabled={loading}>
                                {loading ? <AiOutlineLoading /> : "Update"}
                            </button>
                            <button onClick={() => navigate("/warehouse")}>Cancel</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WarehouseEdit;